import type {ContentRelationshipField, RichTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {RTNode} from '@prismicio/types'
import type {
  AuthorDocumentData,
  BlogPostSliceDefaultPrimaryFaqSectionItem,
  PickContentRelationshipFieldData,
  Simplify,
} from 'prismicio-types'

// Constants
export const LOGO_URL = 'https://www.getmoss.com/moss-logo.png'
export const DEFAULT_AUTHOR_NAME = 'Moss'

/**
 * Heading extracted from content
 */
export type Heading = {
  id: string
  text: string
}

/**
 * Article structured data for SEO
 */
export type ArticleStructuredData = {
  '@context': string
  '@type': string
  headline: string
  image?: string[]
  datePublished: string
  dateModified: string
  author: {
    '@type': string
    name: string
    image?: string
  }
  publisher: {
    '@type': string
    name: string
    logo: {
      '@type': string
      url: string
    }
  }
  articleSection?: string
}

export type FaqStructuredData = {
  '@context': string
  '@type': string
  mainEntity: {
    '@type': string
    name: string
    acceptedAnswer: {
      '@type': string
      text: string
    }
  }[]
}

/**
 * Creates a URL-friendly slug from text
 */
export const createSlugFromText = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

/**
 * Extracts all h3 headings from rich text content for table of contents
 */
export const extractHeadingsFromContent = (content: RTNode[] | null | undefined): Heading[] => {
  const headings: Heading[] = []

  if (!content) {
    return headings
  }

  content.forEach((node: RTNode) => {
    if (node.type === 'heading3' && 'text' in node && node.text) {
      headings.push({
        id: createSlugFromText(node.text),
        text: node.text,
      })
    }
  })

  return headings
}

export const sanitizeJsonLd = (data: ArticleStructuredData | FaqStructuredData | Record<string, unknown>): string =>
  JSON.stringify(data).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026')

/**
 * Generates Article structured data for SEO (schema.org)
 */
export const generateArticleStructuredData = (params: {
  title: RichTextField
  imageUrl: string
  author: ContentRelationshipField<
    'author',
    string,
    PickContentRelationshipFieldData<
      {id: 'author'; fields: ['photo', 'name', 'summary', 'linkedin_link']},
      Simplify<AuthorDocumentData>,
      string
    >
  >
  publishedDate: string | null | undefined
  category: string | null | undefined
  description?: string | null
  url?: string | null
}): ArticleStructuredData | null => {
  const {title, imageUrl, author, publishedDate, category, description, url} = params

  // Extract headline from title
  const firstNode = title?.[0]
  const headline = isFilled.richText(title) && firstNode && 'text' in firstNode ? firstNode.text : ''

  // Get author name
  const authorData = isFilled.contentRelationship(author) && author.data ? (author.data as AuthorDocumentData) : null
  const authorName = authorData?.name || DEFAULT_AUTHOR_NAME

  // Format date
  const datePublished = publishedDate ? new Date(publishedDate).toISOString() : ''

  // Only generate structured data if we have required fields
  if (!headline || !imageUrl || !datePublished) {
    return null
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline,
    image: [imageUrl],
    datePublished,
    dateModified: datePublished, // Use same as published if no modified date available
    author: {
      '@type': 'Person',
      name: authorName,
      ...(authorData?.photo.url && {image: authorData.photo.url}),
    },
    publisher: {
      '@type': 'Organization',
      name: DEFAULT_AUTHOR_NAME,
      logo: {
        '@type': 'ImageObject',
        url: LOGO_URL,
      },
    },
    ...(category && {articleSection: category}),
    ...(description && {description}),
    ...(url && {
      url,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url,
      },
    }),
  }
}

export const generateFaqStructuredData = (
  faqItems: BlogPostSliceDefaultPrimaryFaqSectionItem[],
): FaqStructuredData | null => {
  if (!faqItems || faqItems.length === 0) {
    return null
  }

  const mainEntity = faqItems
    .map((item) => {
      if (!isFilled.richText(item.question_and_answer)) {
        return null
      }

      const nodes = item.question_and_answer
      const questionNode = nodes.find((n) => n.type === 'heading6' && 'text' in n && n.text)
      const answerNode = nodes.find((n) => n.type === 'paragraph' && 'text' in n && n.text)

      if (!questionNode || !answerNode || !('text' in questionNode) || !('text' in answerNode)) {
        return null
      }

      return {
        '@type': 'Question' as const,
        name: questionNode.text,
        acceptedAnswer: {
          '@type': 'Answer' as const,
          text: answerNode.text,
        },
      }
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)

  if (mainEntity.length === 0) {
    return null
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  }
}
